import { themeOptions, useTheme } from "@swivu/shared-ui";
import React, { createContext, useContext } from "react";

type SettingsBaseProps = {
  timezone: string;
  theme: (typeof themeOptions)[number]["value"];
};

type UpdateSettingsProps = {
  updateSettings: (props: Partial<SettingsBaseProps>) => void;
};

export type SettingsProps = SettingsBaseProps & UpdateSettingsProps;

const defaultContext = {
  theme: themeOptions[2].value,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
};

const SettingsContext = createContext<SettingsProps | undefined>({
  ...defaultContext,
  updateSettings: (_props) => null
});

type SettingsContextProviderProps = {
  children: React.ReactNode;
};
export const SettingsContextProvider = ({
  children
}: SettingsContextProviderProps) => {
  const [settings, setSettings] = React.useState<SettingsBaseProps>(
    localStorage.getItem("settings")
      ? {
          ...defaultContext,
          ...JSON.parse(localStorage.getItem("settings") as string)
        }
      : defaultContext
  );

  React.useEffect(() => {
    localStorage.setItem("settings", JSON.stringify(settings));
  }, [settings]);

  useTheme(settings.theme);

  const updateSettings = (
    newSettings: Parameters<UpdateSettingsProps["updateSettings"]>[0]
  ) => {
    setSettings((settings) => ({ ...settings, ...newSettings }));
  };
  return (
    <SettingsContext.Provider value={{ ...settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error(`useSettings must be used within a BoardsContextProvider`);
  }
  return context;
};
